import React, { useState, useRef, useEffect } from 'react';
import { updateUserProfile, fetchUser } from '../services/authService';
import { showErrorToast, showSuccessToast } from '../../../utils/toastHelper';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const MyProfile = () => {
  const { t } = useTranslation('login');

  const { user, setUser } = useAuth();
  const [originalData, setOriginalData] = useState(null);
  const [data, setData] = useState({
    name: '',
    email: '',
    profile_picture: null,
    profile_picture_url: 'images/empty-user.png',
  });

  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      const userData = {
        name: user.name || '',

        email: user.email || '',

        profile_picture_url: user.profile_picture || 'images/empty-user.png',
      };
      setData(userData);
      setOriginalData(userData);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        showErrorToast(t('FILESIZESHOULDBELESSTHAN5MB'));
        return;
      }
      setData((prevData) => ({
        ...prevData,
        profile_picture: file,
        profile_picture_url: URL.createObjectURL(file),
      }));
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    setData(originalData);
    setEditMode(false);
  };

  const handleProfileImageClick = () => {
    if (editMode && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key !== 'profile_picture_url' && data[key] !== null) {
          formData.append(key, data[key]);
        }
      });

      const updateResponse = await updateUserProfile(formData);
      if (updateResponse?.success) {
        // Fetch updated user data
        const userResponse = await fetchUser();
        if (userResponse?.success) {
          setUser(userResponse.user);
          setOriginalData(data);
          showSuccessToast(updateResponse.message);
          setEditMode(false);
        }
      }
    } catch (error) {
      showErrorToast(error.message || 'Something went wront');
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="profile">
      <div className="profile-header">
        <div
          className="profile-image"
          style={{
            position: 'relative',
            cursor: editMode ? 'pointer' : 'default',
          }}
          onClick={handleProfileImageClick}
        >
          <img
            src={data.profile_picture_url}
            alt="Profile"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'images/empty-user.png';
            }}
          />
          {editMode && (
            <div
              className="edit-overlay"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
              }}
            >
              {t('CHANGEPHOTO')}
            </div>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="image/*"
        />

        <button
          className={`edit-button ${editMode ? 'disable-btn' : ''}`}
          disabled={editMode}
          onClick={handleEditClick}
        >
          <img src="images/edit.svg" alt="Edit" />
        </button>
      </div>

      <div className="row">
        {[
          {
            label: t('NAME'),
            name: 'name',
            type: 'text',
            required: true,
            classes: 'col-sm-6',
          },

          {
            label: t('EMAIL'),
            name: 'email',
            type: 'email',
            disabled: true,
            classes: 'col-sm-6',
          },
        ].map((field, index) => (
          <div key={index} className={field.classes}>
            <div className="did-floating-label-content">
              <input
                className="did-floating-input"
                type={field.type}
                placeholder=" "
                name={field.name}
                value={data[field.name]}
                onChange={handleChange}
                disabled={!editMode || field.disabled}
                required={field.required}
              />
              <label className="did-floating-label">
                {field.label}
                {field.required && <span className="required">*</span>}
              </label>
            </div>
          </div>
        ))}

        {/* <div className="col-sm-12">
          <div className="did-floating-label-content about">
            <textarea
              className="did-floating-input"
              placeholder=" "
              name="about"
              rows="4"
              cols="50"
              value={data.about}
              onChange={handleChange}
              disabled={!editMode}
            />
            <label className="did-floating-label">{t('ABOUT')}</label>
          </div>
        </div> */}
      </div>
      {/* 
      <div className="col-sm-4">
        <div className="did-floating-label-content">
          <textarea
            className="did-floating-input"
            placeholder=" "
            name="about"
            rows="4"
            value={data.about}
            onChange={handleChange}
            disabled={!editMode}
          />
          <label className="did-floating-label">{t('ABOUT')}</label>
        </div>
      </div> */}

      {editMode && (
        <div className="bottom-button">
          <button className="save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('SAVING') + '...' : t('SAVE')}
          </button>
          <button className="cancel" onClick={handleCancel} disabled={isSaving}>
            {t('CANCEL')}
          </button>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
